import React, { useState, useEffect, useRef } from "react";
import FinancialIncomeCard from "./components/FinancialIncomeCard";
import IncomeBreakdownCards from "./components/IncomeBreakdownCard";
import IncomeDistributionPieChart from "../common/IncomeDistributionPieChart";
import IncomeBarChart from "../common/IncomeBarChart";
import FinancialStatementTable from "./components/FinancialStatementTable";
import { getDisbursalData } from "../API's/Actioncreator";
import moment from "moment";

function Financial({ selectedMonth, commonFilter, refreshKey, isManualRefresh, setIsManualRefresh }) {
  const abortControllerRef = useRef(null);
  const [financialData, setFinancialData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const cancelAndReset = () => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    setFinancialData(null);
    setLoading(true);
    setError(null);
  };

  useEffect(() => {
    if (!selectedMonth || selectedMonth.length === 0) return;

    cancelAndReset();
    const signal = abortControllerRef.current.signal;

    const startDate = moment(commonFilter?.fromDate, "YYYY-MM-DD").format("DD-MM-YYYY");
    const endDate   = moment(commonFilter?.toDate,   "YYYY-MM-DD").format("DD-MM-YYYY");
    const nbfc      = commonFilter?.nbfc ?? "";

    getDisbursalData(
      startDate,
      endDate,
      nbfc,isManualRefresh,
      (response) => {
        if (signal.aborted) return;
        setLoading(false);
        if (response?.status === 200 && response?.data) {
          setFinancialData(response.data);
        }
      },
      (err) => {
        if (signal.aborted) return;
        setLoading(false);
        setError("Failed to load financial data: " + err?.message);
        console.error("Financial fetch failed:", err);
      },
      signal,
    );
    setIsManualRefresh(false)
    return () => {
      abortControllerRef.current?.abort();
    };
  }, [selectedMonth, commonFilter, refreshKey]);

  // ── REMOVED: if (loading) return <SpinnerLoader />  ──────────────────────
  // Skeleton loaders inside each component handle the loading state instead.

  if (error) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px", color: "#E7000B", fontSize: "16px" }}>
        {error}
      </div>
    );
  }

  return (
    <div>
      <FinancialIncomeCard data={financialData} loading={loading} />
      <IncomeBreakdownCards data={financialData} loading={loading} />

      {/* Charts only render once data is available */}
      {!loading && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: window.innerWidth > 1800 ? "30px" : "20px",
            marginTop: window.innerWidth > 1800 ? "30px" : "20px",
          }}
        >
          <div style={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: "14px", padding: "20px", boxShadow: "0px 1px 1px rgba(0,0,0,0.08), 0px 1px 2px rgba(0,0,0,0.06)" }}>
            <IncomeDistributionPieChart
              title="Income Distribution"
              labels={["Principal", "Interest", "Processing Fee", "Charges"]}
              series={[
                financialData?.financialBreakdown?.principal?.percentage    || 0,
                financialData?.financialBreakdown?.interest?.percentage     || 0,
                financialData?.financialBreakdown?.processingFee?.percentage || 0,
                financialData?.financialBreakdown?.charges?.percentage      || 0,
              ]}
            />
          </div>
          <div style={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: "14px", padding: "20px", boxShadow: "0px 1px 1px rgba(0,0,0,0.08), 0px 1px 2px rgba(0,0,0,0.06)" }}>
            <IncomeBarChart
              title="Income Components Comparison"
              gridType="horizontal"
              categories={["Principal", "Interest", "Processing Fee", "Charges"]}
              series={[{
                name: "Income",
                data: [
                  financialData?.financialBreakdown?.principal?.amount    || 0,
                  financialData?.financialBreakdown?.interest?.amount     || 0,
                  financialData?.financialBreakdown?.processingFee?.amount || 0,
                  financialData?.financialBreakdown?.charges?.amount      || 0,
                ],
              }]}
              yType="currency"
              currencySymbol="₹"
            />
          </div>
        </div>
      )}

      <FinancialStatementTable data={financialData} loading={loading} />
    </div>
  );
}

export default Financial;